import * as THREE from 'three';
import type { BoardNode } from '../../../shared/board/types';
import { BoardGraph } from '../../../shared/board/graph';
import { DistanceCache } from '../../../shared/board/distance-cache';
import { getAvailableMovementOptions } from '../../../shared/game/movement-options';
import type { GameState, Phase } from '../../../shared/game/types';
import type { MapHandle } from '../maps';
import type { GsapRegistry, GsapTimelineHandle } from '../vfx/gsap-registry';

export const PATH_ARROW_STYLE_VERSION = 3;

export interface PathArrowsHandle {
  readonly styleVersion: number;
  readonly group: THREE.Group;
  update(
    state: GameState,
    mapHandle: MapHandle,
    inputLocked: boolean,
    validForkOptions?: readonly string[],
  ): void;
  selectNextNodeId(nextNodeId: string | null): void;
  cycleSelection(direction: -1 | 1): string | null;
  getSelectedNextNodeId(): string | null;
  pickNextNodeId(
    clientX: number,
    clientY: number,
    camera: THREE.Camera,
    canvas: HTMLCanvasElement,
  ): string | null;
  clear(): void;
  dispose(): void;
}

interface CachedGraph {
  readonly nodesRef: readonly unknown[];
  readonly graph: BoardGraph;
  readonly nodeById: Map<string, BoardNode>;
  readonly distanceCache: DistanceCache;
}

const ACTION_PHASES = new Set<Phase>(['item_phase', 'rolling', 'fork_choice']);
const ARROW_LENGTH = 0.5;
const ARROW_HALF_WIDTH = 0.08;
const ARROW_HEAD_HALF_WIDTH = 0.17;
const ARROW_SHOULDER_LENGTH = 0.27;
const ARROW_DEPTH = 0.055;
const ARROW_OVERLAY_CLEARANCE = 0.82;
const ARROW_FILL_RENDER_ORDER = 42;
const ARROW_OUTLINE_RENDER_ORDER = 41;
const CHARACTER_CLEARANCE = 0.38;
const CHARACTER_GAP = 0.24;
const RECOMMENDED_TRAVEL = 0.16;
const BOB_DISTANCE = 0.08;
const STEP_HINT_CANVAS_WIDTH = 512;
const STEP_HINT_CANVAS_HEIGHT = 128;
const STEP_HINT_Y_OFFSET = 1.26;
const STEP_HINT_SCALE_X = 2.2;
const STEP_HINT_SCALE_Y = 0.55;

interface ArrowRecord {
  readonly nextNodeId: string;
  readonly mesh: THREE.Mesh;
  readonly outlineMesh: THREE.Mesh;
  readonly group: THREE.Group;
  readonly bobGroup: THREE.Group;
  readonly travelGroup: THREE.Group;
  readonly recommended: boolean;
  readonly lengthScale: number;
}

function addRoundedCornerPath(
  shape: THREE.Shape,
  fromX: number,
  fromY: number,
  cornerX: number,
  cornerY: number,
  toX: number,
  toY: number,
): void {
  shape.lineTo(fromX, fromY);
  shape.quadraticCurveTo(cornerX, cornerY, toX, toY);
}

function isActionPhase(phase: Phase): boolean {
  return ACTION_PHASES.has(phase);
}

function buildCache(nodesRef: readonly unknown[]): CachedGraph {
  const nodes = nodesRef as readonly BoardNode[];
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const graph = new BoardGraph(Array.from(nodes));
  return {
    nodesRef,
    graph,
    nodeById,
    distanceCache: new DistanceCache(graph),
  };
}

function pickBestTarget(
  connections: readonly { targetId: string }[],
  chestNodeId: string,
  distanceCache: DistanceCache,
): string | null {
  if (connections.length < 2 || chestNodeId === '') return null;

  let bestTargetId: string | null = null;
  let bestDistance = Infinity;
  for (const connection of connections) {
    const distance = distanceCache.getDistance(connection.targetId, chestNodeId);
    if (distance < 0) continue;
    if (
      distance < bestDistance ||
      (distance === bestDistance &&
        (bestTargetId == null || connection.targetId.localeCompare(bestTargetId) < 0))
    ) {
      bestDistance = distance;
      bestTargetId = connection.targetId;
    }
  }

  return bestTargetId;
}

function disposeMaterial(material: THREE.Material | THREE.Material[]): void {
  if (Array.isArray(material)) {
    for (const mat of material) mat.dispose();
    return;
  }
  material.dispose();
}

function formatStepsLeft(steps: number): string {
  const safeSteps = Math.max(0, Math.trunc(steps));
  return safeSteps === 1 ? '1 step left' : `${safeSteps} steps left`;
}

function buildStepHintTexture(text: string): THREE.CanvasTexture | THREE.DataTexture {
  if (typeof document === 'undefined') {
    const texture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    texture.needsUpdate = true;
    return texture;
  }

  const canvas = document.createElement('canvas');
  canvas.width = STEP_HINT_CANVAS_WIDTH;
  canvas.height = STEP_HINT_CANVAS_HEIGHT;
  const context = canvas.getContext('2d');
  if (context == null) {
    const texture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    texture.needsUpdate = true;
    return texture;
  }

  context.clearRect(0, 0, STEP_HINT_CANVAS_WIDTH, STEP_HINT_CANVAS_HEIGHT);
  context.fillStyle = 'rgba(30, 35, 50, 0.90)';
  roundRect(context, 16, 18, STEP_HINT_CANVAS_WIDTH - 32, STEP_HINT_CANVAS_HEIGHT - 36, 42);
  context.fill();
  context.strokeStyle = 'rgba(255, 224, 106, 0.85)';
  context.lineWidth = 6;
  context.stroke();

  context.font = "900 48px 'Nunito Sans', 'Arial Rounded MT Bold', sans-serif";
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.lineJoin = 'round';
  context.strokeStyle = 'rgba(0, 0, 0, 0.85)';
  context.lineWidth = 8;
  context.strokeText(text, STEP_HINT_CANVAS_WIDTH / 2, STEP_HINT_CANVAS_HEIGHT / 2 + 2);
  context.fillStyle = '#FFFFFF';
  context.fillText(text, STEP_HINT_CANVAS_WIDTH / 2, STEP_HINT_CANVAS_HEIGHT / 2 + 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function roundRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + r, y);
  context.lineTo(x + width - r, y);
  context.quadraticCurveTo(x + width, y, x + width, y + r);
  context.lineTo(x + width, y + height - r);
  context.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  context.lineTo(x + r, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - r);
  context.lineTo(x, y + r);
  context.quadraticCurveTo(x, y, x + r, y);
  context.closePath();
}

export function createPathArrows(args: {
  parent: THREE.Group;
  gsapRegistry: GsapRegistry;
  getWorldPositionForNode?: (nodeId: string) => THREE.Vector3 | null;
  getTilePadTopYForNode?: (nodeId: string) => number | null;
}): PathArrowsHandle {
  const group = new THREE.Group();
  group.name = 'path-arrows';
  group.visible = false;
  args.parent.add(group);

  const arrowShape = new THREE.Shape();
  const cornerRadius = 0.018;
  arrowShape.moveTo(-ARROW_HALF_WIDTH + cornerRadius, 0);
  addRoundedCornerPath(
    arrowShape,
    ARROW_HALF_WIDTH - cornerRadius,
    0,
    ARROW_HALF_WIDTH,
    0,
    ARROW_HALF_WIDTH,
    cornerRadius,
  );
  addRoundedCornerPath(
    arrowShape,
    ARROW_HALF_WIDTH,
    ARROW_SHOULDER_LENGTH - cornerRadius,
    ARROW_HALF_WIDTH,
    ARROW_SHOULDER_LENGTH,
    ARROW_HALF_WIDTH + cornerRadius,
    ARROW_SHOULDER_LENGTH,
  );
  addRoundedCornerPath(
    arrowShape,
    ARROW_HEAD_HALF_WIDTH - cornerRadius,
    ARROW_SHOULDER_LENGTH,
    ARROW_HEAD_HALF_WIDTH,
    ARROW_SHOULDER_LENGTH,
    ARROW_HEAD_HALF_WIDTH - cornerRadius,
    ARROW_SHOULDER_LENGTH + cornerRadius,
  );
  addRoundedCornerPath(
    arrowShape,
    cornerRadius,
    ARROW_LENGTH - cornerRadius,
    0,
    ARROW_LENGTH,
    -cornerRadius,
    ARROW_LENGTH - cornerRadius,
  );
  addRoundedCornerPath(
    arrowShape,
    -ARROW_HEAD_HALF_WIDTH + cornerRadius,
    ARROW_SHOULDER_LENGTH + cornerRadius,
    -ARROW_HEAD_HALF_WIDTH,
    ARROW_SHOULDER_LENGTH,
    -ARROW_HEAD_HALF_WIDTH + cornerRadius,
    ARROW_SHOULDER_LENGTH,
  );
  addRoundedCornerPath(
    arrowShape,
    -ARROW_HALF_WIDTH - cornerRadius,
    ARROW_SHOULDER_LENGTH,
    -ARROW_HALF_WIDTH,
    ARROW_SHOULDER_LENGTH,
    -ARROW_HALF_WIDTH,
    ARROW_SHOULDER_LENGTH - cornerRadius,
  );
  addRoundedCornerPath(
    arrowShape,
    -ARROW_HALF_WIDTH,
    cornerRadius,
    -ARROW_HALF_WIDTH,
    0,
    -ARROW_HALF_WIDTH + cornerRadius,
    0,
  );

  const arrowGeometry = new THREE.ExtrudeGeometry(arrowShape, {
    depth: ARROW_DEPTH,
    bevelEnabled: true,
    bevelThickness: 0.02,
    bevelSize: 0.018,
    bevelSegments: 4,
  });
  arrowGeometry.rotateX(Math.PI / 2);
  arrowGeometry.translate(0, ARROW_DEPTH / 2, -ARROW_LENGTH / 2);
  arrowGeometry.computeVertexNormals();

  const normalMaterial = new THREE.MeshStandardMaterial({
    color: 0xffd33f,
    emissive: 0xb66a00,
    emissiveIntensity: 0.55,
    metalness: 0,
    roughness: 0.5,
    envMapIntensity: 0,
    transparent: false,
    opacity: 1,
    blending: THREE.NoBlending,
    depthTest: false,
    depthWrite: false,
    fog: false,
    toneMapped: false,
  });
  const selectedMaterial = new THREE.MeshStandardMaterial({
    color: 0xffec63,
    emissive: 0xffc928,
    emissiveIntensity: 0.95,
    metalness: 0,
    roughness: 0.42,
    envMapIntensity: 0,
    transparent: false,
    opacity: 1,
    blending: THREE.NoBlending,
    depthTest: false,
    depthWrite: false,
    fog: false,
    toneMapped: false,
  });
  const outlineMaterial = new THREE.MeshBasicMaterial({
    color: 0x1b1208,
    transparent: false,
    opacity: 1,
    blending: THREE.NoBlending,
    depthTest: false,
    depthWrite: false,
    fog: false,
    toneMapped: false,
    side: THREE.DoubleSide,
  });

  let disposed = false;
  let cache: CachedGraph | null = null;
  let signature = '';
  const pulseHandles: GsapTimelineHandle[] = [];
  const arrowRecords = new Map<string, ArrowRecord>();
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  let stepHintSprite: THREE.Sprite | null = null;
  let stepHintMaterial: THREE.SpriteMaterial | null = null;
  let stepHintTexture: THREE.CanvasTexture | THREE.DataTexture | null = null;
  let currentOptionIds: string[] = [];
  let recommendedNextNodeId: string | null = null;
  let selectedNextNodeId: string | null = null;
  let selectionScopeKey = '';

  function getCache(mapHandle: MapHandle): CachedGraph {
    const nodesRef = mapHandle.schema.nodes;
    if (cache == null || cache.nodesRef !== nodesRef) {
      cache = buildCache(nodesRef);
    }
    return cache;
  }

  function clearPulses(): void {
    while (pulseHandles.length > 0) {
      pulseHandles.pop()!.dispose();
    }
  }

  function disposeStepHint(): void {
    if (stepHintSprite != null) {
      group.remove(stepHintSprite);
      stepHintSprite = null;
    }
    stepHintMaterial?.dispose();
    stepHintMaterial = null;
    stepHintTexture?.dispose();
    stepHintTexture = null;
  }

  function clear(): void {
    signature = '';
    clearPulses();
    disposeStepHint();
    arrowRecords.clear();
    currentOptionIds = [];
    recommendedNextNodeId = null;
    selectedNextNodeId = null;
    selectionScopeKey = '';
    group.clear();
    group.visible = false;
  }

  function getNodePosition(node: BoardNode): THREE.Vector3 {
    const resolved = args.getWorldPositionForNode?.(node.id);
    if (resolved != null) return resolved.clone();
    return new THREE.Vector3(node.position.x, node.position.y, node.position.z);
  }

  function getNodeTopY(node: BoardNode, fallbackPosition: THREE.Vector3): number {
    const resolved = args.getTilePadTopYForNode?.(node.id);
    return resolved ?? fallbackPosition.y;
  }

  function updateArrowMaterials(): void {
    for (const record of arrowRecords.values()) {
      const selected = record.nextNodeId === selectedNextNodeId;
      record.mesh.material = selected ? selectedMaterial : normalMaterial;
      record.outlineMesh.visible = true;
      record.outlineMesh.scale.set(
        selected ? 1.2 : 1.14,
        selected ? 1.05 : 1.03,
        selected ? 1.1 : 1.06,
      );
      const targetScale = selected ? 1.16 : 1;
      record.group.scale.set(targetScale, targetScale, record.lengthScale * targetScale);
    }
  }

  function selectNextNodeId(nextNodeId: string | null): void {
    if (nextNodeId != null && !currentOptionIds.includes(nextNodeId)) return;
    selectedNextNodeId = nextNodeId;
    updateArrowMaterials();
  }

  function cycleSelection(direction: -1 | 1): string | null {
    if (currentOptionIds.length === 0) return null;
    const fallback = recommendedNextNodeId ?? currentOptionIds[0] ?? null;
    const current = selectedNextNodeId ?? fallback;
    const currentIndex = current == null ? -1 : currentOptionIds.indexOf(current);
    const nextIndex =
      currentIndex < 0
        ? 0
        : (currentIndex + direction + currentOptionIds.length) % currentOptionIds.length;
    const nextNodeId = currentOptionIds[nextIndex] ?? null;
    selectNextNodeId(nextNodeId);
    return selectedNextNodeId;
  }

  function getSelectedNextNodeId(): string | null {
    return selectedNextNodeId;
  }

  function pickNextNodeId(
    clientX: number,
    clientY: number,
    camera: THREE.Camera,
    canvas: HTMLCanvasElement,
  ): string | null {
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -(((clientY - rect.top) / rect.height) * 2 - 1),
    );
    raycaster.setFromCamera(ndc, camera);
    const hits = raycaster.intersectObjects(group.children, true);
    for (const hit of hits) {
      let current: THREE.Object3D | null = hit.object;
      while (current != null && current !== group) {
        const nextNodeId = current.userData.nextNodeId;
        if (typeof nextNodeId === 'string') return nextNodeId;
        current = current.parent;
      }
    }
    return null;
  }

  function addArrow(from: BoardNode, to: BoardNode, highlighted: boolean, index: number): void {
    const fromPos = getNodePosition(from);
    const toPos = getNodePosition(to);
    const delta = new THREE.Vector3(toPos.x - fromPos.x, 0, toPos.z - fromPos.z);
    const distance = delta.length();
    if (distance < 0.001) return;

    const direction = delta.normalize();
    const usableLength = Math.max(0.35, distance - CHARACTER_CLEARANCE - CHARACTER_GAP);
    const lengthScale = Math.min(1, Math.max(0.52, usableLength / ARROW_LENGTH));
    const centerDistance = CHARACTER_CLEARANCE + CHARACTER_GAP + (ARROW_LENGTH * lengthScale) / 2;
    const centerT = THREE.MathUtils.clamp(centerDistance / distance, 0, 1);
    const fromTopY = getNodeTopY(from, fromPos);
    const toTopY = getNodeTopY(to, toPos);
    const basePosition = new THREE.Vector3(
      fromPos.x,
      THREE.MathUtils.lerp(fromTopY, toTopY, centerT) + ARROW_OVERLAY_CLEARANCE,
      fromPos.z,
    ).addScaledVector(direction, centerDistance);
    const arrow = new THREE.Group();
    arrow.name = highlighted ? `path-arrow:${to.id}:recommended` : `path-arrow:${to.id}`;
    arrow.position.copy(basePosition);
    arrow.rotation.y = Math.atan2(direction.x, direction.z);
    arrow.scale.set(1, 1, lengthScale);
    arrow.userData.nextNodeId = to.id;
    arrow.userData.kind = 'path-arrow';

    const bobGroup = new THREE.Group();
    bobGroup.name = `${arrow.name}:bob`;
    bobGroup.userData.nextNodeId = to.id;
    bobGroup.userData.kind = 'path-arrow';

    const travelGroup = new THREE.Group();
    travelGroup.name = `${arrow.name}:travel`;
    travelGroup.userData.nextNodeId = to.id;
    travelGroup.userData.kind = 'path-arrow';

    const mesh = new THREE.Mesh(arrowGeometry, normalMaterial);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = ARROW_FILL_RENDER_ORDER;
    mesh.userData.nextNodeId = to.id;
    mesh.userData.kind = 'path-arrow-fill';
    const outlineMesh = new THREE.Mesh(arrowGeometry, outlineMaterial);
    outlineMesh.castShadow = false;
    outlineMesh.receiveShadow = false;
    outlineMesh.renderOrder = ARROW_OUTLINE_RENDER_ORDER;
    outlineMesh.scale.set(1.16, 1.04, 1.08);
    outlineMesh.visible = false;
    outlineMesh.userData.nextNodeId = to.id;
    outlineMesh.userData.kind = 'path-arrow-outline';
    travelGroup.add(outlineMesh);
    travelGroup.add(mesh);
    bobGroup.add(travelGroup);
    arrow.add(bobGroup);
    group.add(arrow);
    arrowRecords.set(to.id, {
      nextNodeId: to.id,
      mesh,
      outlineMesh,
      group: arrow,
      bobGroup,
      travelGroup,
      recommended: highlighted,
      lengthScale,
    });

    const handle = args.gsapRegistry.create(`path-arrows:pulse:${index}`);
    handle.timeline.to(
      bobGroup.position,
      {
        y: BOB_DISTANCE,
        duration: 0.9,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      },
      index * 0.08,
    );
    if (highlighted) {
      handle.timeline.to(
        travelGroup.position,
        {
          z: RECOMMENDED_TRAVEL,
          duration: 0.62,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        },
        0,
      );
    }
    pulseHandles.push(handle);
  }

  function addStepHint(node: BoardNode, remainingSteps: number): void {
    const text = formatStepsLeft(remainingSteps);
    const texture = buildStepHintTexture(text);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    const nodePosition = getNodePosition(node);
    const topY = getNodeTopY(node, nodePosition);
    sprite.name = 'path-arrows:steps-left';
    sprite.position.set(nodePosition.x, topY + STEP_HINT_Y_OFFSET, nodePosition.z);
    sprite.scale.set(STEP_HINT_SCALE_X, STEP_HINT_SCALE_Y, 1);
    sprite.renderOrder = 20;
    sprite.userData.kind = 'path-steps-left';
    sprite.userData.text = text;
    group.add(sprite);
    stepHintSprite = sprite;
    stepHintMaterial = material;
    stepHintTexture = texture;
  }

  function update(
    state: GameState,
    mapHandle: MapHandle,
    inputLocked: boolean,
    validForkOptions?: readonly string[],
  ): void {
    if (disposed) return;
    if (inputLocked || !isActionPhase(state.phase)) {
      clear();
      return;
    }

    const currentPlayerId = state.turnOrder[state.currentPlayerIndex] ?? null;
    if (currentPlayerId == null) {
      clear();
      return;
    }

    const player = state.players.get(currentPlayerId);
    if (player == null) {
      clear();
      return;
    }

    const graphCache = getCache(mapHandle);
    const node = graphCache.nodeById.get(player.nodeId);
    if (node == null || node.connections.length < 1) {
      clear();
      return;
    }

    const allowBacktrack = state.phase === 'item_phase' || state.phase === 'rolling';
    const optionIds =
      validForkOptions != null
        ? validForkOptions
        : getAvailableMovementOptions(
            graphCache.graph,
            currentPlayerId,
            node.id,
            state.lastMoveByPlayer,
            { allowBacktrack },
          ).map((option) => option.node.id);
    const optionIdSet = new Set(optionIds);
    const connections = optionIds
      .map((targetId) => node.connections.find((conn) => conn.targetId === targetId))
      .filter((conn): conn is BoardNode['connections'][number] => conn != null);
    if (connections.length === 0) {
      clear();
      return;
    }

    const bestTargetId = pickBestTarget(
      connections,
      state.treasureChestNodeId,
      graphCache.distanceCache,
    );
    const nextSignature = [
      state.phase,
      currentPlayerId,
      player.nodeId,
      state.treasureChestNodeId,
      bestTargetId ?? '',
      state.phase === 'fork_choice' ? (state.pendingForkChoice?.id ?? '') : '',
      state.phase === 'fork_choice' ? String(state.pendingForkChoice?.remainingSteps ?? '') : '',
      connections.map((conn) => `${conn.targetId}:${conn.type}`).join('|'),
    ].join('::');
    if (nextSignature === signature) {
      group.visible = true;
      return;
    }

    const nextSelectionScopeKey = `${currentPlayerId}:${player.nodeId}`;
    const resetSelection = nextSelectionScopeKey !== selectionScopeKey;
    clearPulses();
    disposeStepHint();
    group.clear();
    arrowRecords.clear();
    signature = nextSignature;
    currentOptionIds = connections.map((conn) => conn.targetId);
    const retainedSelection = resetSelection ? null : selectedNextNodeId;
    recommendedNextNodeId = bestTargetId ?? retainedSelection ?? currentOptionIds[0] ?? null;
    if (
      resetSelection ||
      selectedNextNodeId == null ||
      !currentOptionIds.includes(selectedNextNodeId)
    ) {
      selectedNextNodeId = recommendedNextNodeId ?? currentOptionIds[0] ?? null;
    }
    selectionScopeKey = nextSelectionScopeKey;

    connections.forEach((connection, index) => {
      if (!optionIdSet.has(connection.targetId)) return;
      const target = graphCache.nodeById.get(connection.targetId);
      if (target == null) return;
      addArrow(node, target, connection.targetId === recommendedNextNodeId, index);
    });
    if (
      state.phase === 'fork_choice' &&
      state.pendingForkChoice?.playerId === currentPlayerId &&
      state.pendingForkChoice.atNodeId === node.id
    ) {
      addStepHint(node, state.pendingForkChoice.remainingSteps);
    }
    updateArrowMaterials();
    group.visible = group.children.length > 0;
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    clear();
    args.parent.remove(group);
    arrowGeometry.dispose();
    disposeMaterial(normalMaterial);
    disposeMaterial(selectedMaterial);
    disposeMaterial(outlineMaterial);
  }

  return {
    styleVersion: PATH_ARROW_STYLE_VERSION,
    group,
    update,
    selectNextNodeId,
    cycleSelection,
    getSelectedNextNodeId,
    pickNextNodeId,
    clear,
    dispose,
  };
}
